/**
 * Voice: one pointer (or the drone's continuous voice). Builds the per-voice chain
 *   units (sample slots / grains / oscillators, unison + sub/fifth/octave) → sum → drive (buzz/growl) →
 *   filter (+LFO) → tilt shelf → breath gain (LFO + DC) → envelope gain → StereoPanner → Analyser → drone bus
 * and maps gestures (radiusTo / velocityTo), drift and idle evolution onto it. Every change is a ramp.
 */
import { hzToMidi, type Drone } from '@tonist/shared';
import { audioLog, fmt, fmtHz } from './log';
import type { WorkletFlags } from './context';
import { VoiceFilter } from './filter';
import { FreezeUnit, type FreezeSample } from './freezeUnit';
import { LfoRoute, Perlin1D, ticker } from './modulation';
import {
  attackCurve, clamp, dbToGain, driveCurve, holdParam, normaliseVelocity, radiusBrightness, radiusJivari, radiusTilt, targetTau, type F32, type MacroMap, unisonCount, unisonOffsets, unisonPans, FADE_IN, FADE_OUT } from './params';
import { SampledUnit } from './sampledUnit';
import { LiveStretchUnit, type StretchSample } from './liveStretchUnit';
import type { RootBuffer } from './sampler';
import { AdditiveUnit, FmUnit, NoiseUnit, ShepardUnit, WavetableUnit } from './synth/units';
import type { GestureState, VoiceHandle } from './types';
import type { SourceUnit, UnitConfig } from './units';

type SourceLayer = Drone['sources'][number];
type SampleLayer = Extract<SourceLayer, { kind: 'sample' }>;
type FreezeLayer = Extract<SourceLayer, { kind: 'freeze' }>;
type SynthLayer = Extract<SourceLayer, { kind: 'synth' }>;

export type PreparedSource =
  | { kind: 'sample'; layer: SampleLayer; roots: RootBuffer[]; single: boolean }
  | { kind: 'freeze'; layer: FreezeLayer; sample: FreezeSample }
  /** pitch engine "granular": the freeze worklet scanning the loop at real time, grains resampled to the pitch */
  | { kind: 'granular'; layer: SampleLayer; sample: FreezeSample }
  /** pitch engine "live-stretch": per-voice real-time phase vocoder */
  | { kind: 'stretch'; layer: SampleLayer; sample: StretchSample; formant: boolean }
  | { kind: 'synth'; layer: SynthLayer }
  | { kind: 'fallback'; layer: SourceLayer; reason: string };

/** Synth oscillators peak near full scale while samples are normalised to −18 LUFS: trim them to match. */
const SYNTH_LEVEL_TRIM = 0.3;

export interface VoiceInit {
  id: number;
  droneId: string;
  ctx: AudioContext;
  drone: Drone;
  macros: MacroMap;
  sources: PreparedSource[];
  destination: AudioNode;
  hz: number;
  gesture: GestureState;
  attackScale?: number;
  continuous: boolean;
  tier0: boolean;
  worklets: WorkletFlags;
}

let seedCounter = 1;
const nextSeed = (): number => (seedCounter = (seedCounter * 1103515245 + 12345) % 2147483647);

function makeSynth(cfg: UnitConfig, layer: SynthLayer): SourceUnit {
  switch (layer.engine) {
    case 'additive': return new AdditiveUnit(cfg, layer.partials);
    case 'wavetable': return new WavetableUnit(cfg, layer.wavetable ?? 'saw');
    case 'fm': return new FmUnit(cfg, layer.fm?.ratio ?? 2, layer.fm?.index ?? 2);
    case 'shepard': return new ShepardUnit(cfg, layer.shepard?.rate ?? 0.1, layer.shepard?.octaves ?? 6);
    case 'noise': return new NoiseUnit(cfg);
  }
}

export class Voice implements VoiceHandle {
  readonly id: number;
  readonly droneId: string;
  readonly continuous: boolean;
  readonly startedAt: number;
  done = false;
  onDone: (() => void) | null = null;

  private readonly ctx: AudioContext;
  private drone: Drone;
  private macros: MacroMap;
  private readonly tier0: boolean;
  private readonly worklets: WorkletFlags;
  private _hz: number;
  private _latched = false;
  private _releasing = false;
  private idle = false;
  private gesture: GestureState;

  private readonly units: SourceUnit[] = [];
  /** one gain per unit (between the unit and the sum) so units can be crossfaded when the drone finishes loading */
  private readonly unitGains: GainNode[] = [];
  /** kinds of the units currently sounding, for meters and logs */
  sourceKinds: string[] = [];
  /** true while the voice plays stand-in additive units because its drone was still decoding */
  usesFallback = false;
  private readonly freezeUnits: Array<{ unit: FreezeUnit; layer: FreezeLayer }> = [];
  private readonly panners: StereoPannerNode[] = [];
  private readonly sum: GainNode;
  private readonly drive: { dry: GainNode; pre: GainNode; shaper: WaveShaperNode; wet: GainNode } | null = null;
  private readonly filter: VoiceFilter;
  private readonly shelf: BiquadFilterNode;
  private readonly breath: GainNode;
  private breathLfo: LfoRoute | null = null;
  private readonly env: GainNode;
  private readonly panner: StereoPannerNode;
  private readonly analyser: AnalyserNode;
  private readonly vibrato: { osc: OscillatorNode; depth: GainNode } | null = null;
  private readonly driftDetune: ConstantSourceNode;
  private untick: (() => void) | null = null;
  private disposeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly scratch: F32;

  constructor(init: VoiceInit) {
    const { ctx, drone, macros } = init;
    this.id = init.id;
    this.droneId = init.droneId;
    this.continuous = init.continuous;
    this.ctx = ctx;
    this.drone = drone;
    this.macros = macros;
    this.tier0 = init.tier0;
    this.worklets = init.worklets;
    this._hz = init.hz;
    this.gesture = init.gesture;
    const now = ctx.currentTime;
    this.startedAt = now;
    this.scratch = new Float32Array(512);
    const seed = nextSeed();

    this.sum = ctx.createGain();
    this.filter = new VoiceFilter(ctx, drone.filter, macros, seed);
    const vTo = drone.gesture.velocityTo;
    if (vTo === 'buzz' || vTo === 'growl') {
      const dry = ctx.createGain();
      const pre = ctx.createGain();
      const shaper = ctx.createWaveShaper();
      shaper.curve = driveCurve(vTo);
      shaper.oversample = '2x';
      const wet = ctx.createGain();
      wet.gain.value = 0;
      this.sum.connect(dry); dry.connect(this.filter.input);
      this.sum.connect(pre); pre.connect(shaper); shaper.connect(wet); wet.connect(this.filter.input);
      this.drive = { dry, pre, shaper, wet };
    } else {
      this.sum.connect(this.filter.input);
    }
    this.shelf = ctx.createBiquadFilter();
    this.shelf.type = 'highshelf';
    this.shelf.frequency.value = 2500;
    this.shelf.gain.value = macros.tiltDb;
    this.breath = ctx.createGain();
    const breathDepth = this.breathDepth();
    this.breath.gain.value = 1 - breathDepth / 2;
    if (breathDepth > 0 && drone.breath.rateHz > 0) {
      this.breathLfo = new LfoRoute(ctx, {
        shape: drone.breath.shape, rateHz: drone.breath.rateHz, depth: breathDepth / 2,
        phase: drone.breath.phasePerVoice ? (seed % 1000) / 1000 : 0, seed,
      }, this.breath.gain);
    }
    this.env = ctx.createGain();
    this.env.gain.value = 0;
    this.panner = ctx.createStereoPanner();
    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 512;
    this.analyser.smoothingTimeConstant = 0.5;
    this.filter.output.connect(this.shelf);
    this.shelf.connect(this.breath);
    this.breath.connect(this.env);
    this.env.connect(this.panner);
    this.panner.connect(this.analyser);
    this.analyser.connect(init.destination);

    this.driftDetune = ctx.createConstantSource();
    this.driftDetune.offset.value = 0;
    this.driftDetune.start(now);

    for (const src of init.sources) this.buildUnits(src);
    this.usesFallback = init.sources.some((src) => src.kind === 'fallback');
    for (const u of this.units) u.attachDetuneModulator(this.driftDetune);

    if (vTo === 'vibrato') {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 5.5;
      const depth = ctx.createGain();
      depth.gain.value = 0;
      osc.connect(depth);
      osc.start(now);
      for (const u of this.units) u.attachDetuneModulator(depth);
      this.vibrato = { osc, depth };
    }

    for (const u of this.units) u.start(now, init.hz);
    this.filter.setPitch(init.hz, now, 0);
    this.setGesture(init.gesture);
    audioLog.log('voice', () => `#${this.id} ${init.continuous ? 'continuous' : 'noteOn'} ${this.droneId} ${fmtHz(init.hz)} units=[${this.sourceKinds.join(',')}]${this.usesFallback ? ' (STAND-IN: drone still loading)' : ''} filter=${drone.filter.type} attack=${(drone.envelope.attackSec * (init.attackScale ?? 1)).toFixed(2)}s`);

    // attack: linear / exponential-ish / s-curve swell
    const a = Math.max(0.005, drone.envelope.attackSec * (init.attackScale ?? 1));
    const curve = drone.envelope.swellCurve;
    if (curve === 'lin') this.env.gain.linearRampToValueAtTime(1, now + a);
    else this.env.gain.setValueCurveAtTime(attackCurve(curve), now, a);

    this.startDrift(seed);
  }

  private breathDepth(): number {
    return clamp(this.drone.breath.depth * this.macros.lfoDepthScale, 0, 1);
  }

  private buildUnits(src: PreparedSource): void {
    const { drone, macros } = this;
    const layer = src.layer;
    const level = dbToGain(layer.levelDb);
    const base = layer.semitoneOffset * 100;
    const shepard = src.kind === 'synth' && src.layer.engine === 'shepard';
    const n = shepard || src.kind === 'freeze' ? 1 : unisonCount(drone.layers.unison.voices, macros.densityScale, this.tier0 ? 2 : 7);
    const offsets = unisonOffsets(n, drone.layers.unison.detuneCents);
    const pans = unisonPans(n, drone.layers.unison.spread);
    for (let i = 0; i < n; i++) this.addUnit(src, base + offsets[i]!, level / Math.sqrt(n), pans[i]!);
    const L = drone.layers;
    if (L.sub.on) this.addUnit(src, base - 1200, level * dbToGain(L.sub.levelDb), 0);
    if (L.fifth.on) this.addUnit(src, base + 702, level * dbToGain(L.fifth.levelDb), 0);
    if (L.octaveUp.on) this.addUnit(src, base + 1200, level * dbToGain(L.octaveUp.levelDb), 0);
  }

  private addUnit(src: PreparedSource, cents: number, level: number, pan: number): void {
    const trimmed = src.kind === 'synth' || src.kind === 'fallback' ? level * SYNTH_LEVEL_TRIM : level;
    const cfg: UnitConfig = { ctx: this.ctx, cents, level: trimmed, phaseScatter: this.drone.layers.unison.phaseScatter, seed: nextSeed() };
    let unit: SourceUnit;
    switch (src.kind) {
      case 'sample':
        unit = new SampledUnit(cfg, src.roots, src.single);
        break;
      case 'granular': {
        // time-domain granular pitch shift: playhead at real speed (stretch 1), short Hann grains, 2x overlap
        const layer: FreezeLayer = {
          kind: 'freeze', sampleId: src.layer.sampleId, rootMidi: src.layer.rootMidi, position: 0,
          grain: { sizeMs: 70, density: 30, spraySec: 0, pitchJitterCents: 0, texture: 0.5 }, stretch: 1,
          semitoneOffset: src.layer.semitoneOffset, levelDb: src.layer.levelDb,
        };
        // 2x-overlap Hann grains sum ~7 dB below the plain loop: bring the mode to the same loudness as the others
        if (this.worklets.granular) unit = new FreezeUnit({ ...cfg, level: cfg.level * 2.2 }, src.sample, layer, 1, { ...this.drone.gesture, radiusTo: this.drone.gesture.radiusTo === 'grainSize' ? 'brightness' : this.drone.gesture.radiusTo });
        else unit = new SampledUnit(cfg, [{ midi: hzToMidi(src.sample.rootHz), buffer: src.sample.buffer, loopStart: src.sample.loopStart / src.sample.sampleRate, loopEnd: src.sample.loopEnd / src.sample.sampleRate }], true);
        break;
      }
      case 'stretch':
        unit = new LiveStretchUnit(cfg, src.sample, src.formant);
        break;
      case 'freeze':
        if (this.worklets.granular) {
          const fu = new FreezeUnit(cfg, src.sample, src.layer, this.macros.densityScale, this.drone.gesture);
          this.freezeUnits.push({ unit: fu, layer: src.layer });
          unit = fu;
        } else {
          const s = src.sample;
          unit = new SampledUnit(cfg, [{ midi: hzToMidi(s.rootHz), buffer: s.buffer, loopStart: s.loopStart / s.sampleRate, loopEnd: s.loopEnd / s.sampleRate }], true);
        }
        break;
      case 'synth':
        unit = makeSynth(cfg, src.layer);
        break;
      default:
        unit = new AdditiveUnit(cfg);
    }
    const g = this.ctx.createGain();
    g.gain.value = 1;
    unit.output.connect(g);
    if (pan !== 0) {
      const p = this.ctx.createStereoPanner();
      p.pan.value = pan;
      g.connect(p);
      p.connect(this.sum);
      this.panners.push(p);
    } else {
      g.connect(this.sum);
    }
    this.units.push(unit);
    this.unitGains.push(g);
    this.sourceKinds.push(src.kind);
  }

  /**
   * Replace stand-in units with the real ones once the drone's samples have decoded (crossfade, same pitch and
   * gesture, the handle stays valid). Called by the engine for voices that were created while loading.
   */
  upgradeSources(sources: PreparedSource[], force = false): void {
    if (this.done || this._releasing || (!this.usesFallback && !force)) return;
    const now = this.ctx.currentTime;
    const oldUnits = this.units.splice(0), oldGains = this.unitGains.splice(0);
    this.sourceKinds = [];
    this.freezeUnits.length = 0;
    for (const src of sources) this.buildUnits(src);
    const fresh = this.units.slice(oldUnits.length === 0 ? 0 : 0);
    for (const u of fresh) { u.attachDetuneModulator(this.driftDetune); if (this.vibrato) u.attachDetuneModulator(this.vibrato.depth); }
    const XF = 0.4;
    for (const g of this.unitGains) { g.gain.cancelScheduledValues(now); g.gain.setValueCurveAtTime(FADE_IN, now, XF); }
    for (const u of fresh) u.start(now, this._hz);
    this.filter.setPitch(this._hz, now, 0);
    this.setGesture(this.gesture);
    for (const g of oldGains) { g.gain.cancelScheduledValues(now); g.gain.setValueCurveAtTime(FADE_OUT, now, XF); }
    for (const u of oldUnits) u.stop(now + XF + 0.05);
    setTimeout(() => { for (const u of oldUnits) u.dispose(); for (const g of oldGains) { try { g.disconnect(); } catch { /* gone */ } } }, (XF + 0.3) * 1000);
    this.usesFallback = sources.some((src) => src.kind === 'fallback');
    audioLog.log('voice', () => `#${this.id} swapped units → [${this.sourceKinds.join(',')}] at ${fmtHz(this._hz)} (${XF}s crossfade)`);
  }

  get currentGesture(): GestureState { return this.gesture; }

  private startDrift(seed: number): void {
    const m = this.drone.motion;
    if (!(m.driftAmount > 0) && !m.idleEvolve) return;
    const perlins = new Map<string, Perlin1D>();
    for (const t of ['cutoff', 'detune', 'pan', 'breath']) perlins.set(t, new Perlin1D(seed + t.length * 131));
    let phase = (seed % 100) / 10;
    this.untick = ticker.add((now, dt) => {
      const mo = this.drone.motion;
      const idleBoost = this.idle && this._latched && mo.idleEvolve ? 0.25 : 0;
      const amt = clamp(mo.driftAmount * this.macros.driftScale + idleBoost, 0, 1.5);
      if (amt <= 0) return;
      phase += Math.max(0.01, mo.driftRateHz) * dt;
      const targets = mo.targets.length ? mo.targets : ['cutoff'];
      for (const target of targets) {
        const v = perlins.get(target)!.at(phase);
        switch (target) {
          case 'cutoff': this.filter.setDrift(Math.pow(2, v * amt), now); break;
          case 'detune': targetTau(this.driftDetune.offset, v * amt * 12, now, 0.2); break;
          case 'pan': targetTau(this.panner.pan, v * amt * 0.5, now, 0.2); break;
          case 'breath': this.breathLfo?.setDepth((this.breathDepth() / 2) * clamp(1 + v * amt, 0, 2), now, 0.3); break;
        }
      }
    });
  }

  // ---- VoiceHandle -------------------------------------------------------------------------------------------

  get hz(): number { return this._hz; }
  get latched(): boolean { return this._latched; }
  get releasing(): boolean { return this._releasing; }

  setPitch(hz: number, glideSec: number): void {
    if (!Number.isFinite(hz) || hz <= 0) { audioLog.error('pitch', `#${this.id} setPitch rejected non-finite hz=${hz}`); return; }
    audioLog.throttled('pitch', `p${this.id}`, 8, () => `#${this.id} pitch ${fmtHz(this._hz)} → ${fmtHz(hz)} glide=${glideSec.toFixed(2)}s`);
    if (this.done || !(hz > 0)) return;
    this._hz = hz;
    const t = this.ctx.currentTime;
    for (const u of this.units) u.setPitch(hz, glideSec, t);
    this.filter.setPitch(hz, t, glideSec);
  }

  setGesture(g: GestureState): void {
    if (this.done) return;
    this.gesture = g;
    const t = this.ctx.currentTime;
    const r = clamp(g.radius, 0, 1);
    const vn = normaliseVelocity(g.velocity);
    audioLog.throttled('gesture', `v${this.id}`, 4, () => `#${this.id} gesture radius=${fmt(r)}→${this.drone.gesture.radiusTo}${this.drone.gesture.radiusTo === 'jivari' ? `(fb ${fmt(radiusJivari(r))})` : this.drone.gesture.radiusTo === 'brightness' ? `(×${fmt(radiusBrightness(r))})` : ''} velocity=${fmt(vn)}→${this.drone.gesture.velocityTo}`);
    switch (this.drone.gesture.radiusTo) {
      case 'brightness': this.filter.setBrightness(radiusBrightness(r), t); break;
      case 'jivari':
        if (this.filter.type === 'comb') this.filter.setJivari(radiusJivari(r), t);
        else this.filter.setBrightness(radiusBrightness(r), t);
        break;
      case 'tilt': targetTau(this.shelf.gain, this.macros.tiltDb + radiusTilt(r), t, 0.08); break;
      case 'grainSize': break; // handled by the freeze units below
    }
    switch (this.drone.gesture.velocityTo) {
      case 'vibrato':
        if (this.vibrato) targetTau(this.vibrato.depth.gain, 25 * vn, t, 0.15);
        break;
      case 'buzz':
      case 'growl':
        if (this.drive) {
          targetTau(this.drive.wet.gain, vn, t, 0.1);
          targetTau(this.drive.dry.gain, 1 - 0.7 * vn, t, 0.1);
          targetTau(this.drive.pre.gain, 1 + 6 * vn, t, 0.1);
        }
        break;
      default: break;
    }
    for (const u of this.units) u.setGesture?.(g, t);
  }

  release(seconds = this.drone.envelope.releaseSec): void {
    if (this._releasing || this.done) return;
    this._releasing = true;
    audioLog.log('voice', () => `#${this.id} release over ${seconds.toFixed(2)}s at ${fmtHz(this._hz)}`);
    this._latched = false;
    const t = this.ctx.currentTime;
    const rel = Math.max(0.05, seconds);
    holdParam(this.env.gain, t);
    this.env.gain.setTargetAtTime(0, t, rel / 4.6);
    for (const u of this.units) u.stop(t + rel + 0.5);
    this.disposeTimer = setTimeout(() => this.dispose(), (rel + 0.6) * 1000);
  }

  latch(on: boolean): void {
    if (this.done) return;
    if (on) { if (!this._releasing) this._latched = true; }
    else if (this._latched) { this._latched = false; if (!this.continuous) this.release(); }
  }

  // ---- engine-side helpers -------------------------------------------------------------------------------------

  setIdle(idle: boolean): void { this.idle = idle; }

  /** RMS-derived level 0..1 for meters. */
  amplitude(): number {
    if (this.done) return 0;
    this.analyser.getFloatTimeDomainData(this.scratch);
    let s = 0;
    for (let i = 0; i < this.scratch.length; i++) { const v = this.scratch[i]!; s += v * v; }
    return clamp(Math.sqrt(s / this.scratch.length) * 3, 0, 1);
  }

  /** Byte spectrum (256 bins × 93.75 Hz at 48 kHz) from the voice analyser. */
  spectrum(out: Uint8Array<ArrayBuffer>): boolean {
    if (this.done) return false;
    this.analyser.getByteFrequencyData(out);
    return true;
  }

  waveform(out: Float32Array): boolean {
    if (this.done) return false;
    this.analyser.getFloatTimeDomainData(this.scratch);
    const n = Math.min(out.length, this.scratch.length);
    for (let i = 0; i < n; i++) out[i] = this.scratch[i]!;
    return true;
  }

  /** Live editor edits for parameters that do not need a new voice. */
  applyDrone(drone: Drone, macros: MacroMap): void {
    if (this.done) return;
    this.drone = drone;
    this.macros = macros;
    const t = this.ctx.currentTime;
    this.filter.update(drone.filter, macros, t);
    if (drone.gesture.radiusTo !== 'tilt') targetTau(this.shelf.gain, macros.tiltDb, t, 0.2);
    const bd = this.breathDepth();
    targetTau(this.breath.gain, 1 - bd / 2, t, 0.2);
    if (this.breathLfo) { this.breathLfo.setDepth(bd / 2, t); this.breathLfo.setRate(drone.breath.rateHz, t); }
    for (const f of this.freezeUnits) {
      const layer = drone.sources.find((s): s is FreezeLayer => s.kind === 'freeze' && s.sampleId === f.layer.sampleId) ?? f.layer;
      f.unit.update(layer, macros.densityScale, t);
    }
    this.setGesture(this.gesture);
  }

  dispose(): void {
    if (this.done) return;
    this.done = true;
    audioLog.log('voice', `#${this.id} disposed`);
    if (this.disposeTimer) clearTimeout(this.disposeTimer);
    this.untick?.();
    for (const u of this.units) u.dispose();
    this.breathLfo?.dispose();
    this.filter.dispose();
    const now = this.ctx.currentTime;
    try { this.vibrato?.osc.stop(now); } catch { /* stopped */ }
    try { this.driftDetune.stop(now); } catch { /* stopped */ }
    const nodes: Array<AudioNode | undefined> = [
      this.sum, this.shelf, this.breath, this.env, this.panner, this.analyser, this.driftDetune,
      this.drive?.dry, this.drive?.pre, this.drive?.shaper, this.drive?.wet, this.vibrato?.osc, this.vibrato?.depth, ...this.panners,
    ];
    for (const n of nodes) { try { n?.disconnect(); } catch { /* gone */ } }
    this.onDone?.();
  }
}
